import { useState, useMemo } from 'react';
import { Student } from '../../types';

export const useStudent = (
    students: Student[],
    onToggleStatus: (studentId: string, newStatus: 'active' | 'inactive', reason?: string) => void
) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState<string>('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');
    const [viewingStudent, setViewingStudent] = useState<Student | null>(null);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
    const [statusReason, setStatusReason] = useState('');
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedImageUrl, setSelectedImageUrl] = useState('');
    const [selectedImageAlt, setSelectedImageAlt] = useState('');

    // Filter and search students
    const filteredStudents = useMemo(() => {
        return students.filter(student => {
            const matchesSearch =
                student.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                student.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                student.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                student.studentId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                student.academicInfo.currentGrade.toLowerCase().includes(searchTerm.toLowerCase()) ||
                student.academicInfo.section.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesStatus = filterStatus === 'all' || student.academicInfo.status === filterStatus;

            return matchesSearch && matchesStatus;
        });
    }, [students, searchTerm, filterStatus]);

    // Pagination
    const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedStudents = filteredStudents.slice(startIndex, startIndex + itemsPerPage);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleItemsPerPageChange = (value: string) => {
        setItemsPerPage(parseInt(value));
        setCurrentPage(1);
    };

    const handleStatusToggle = (student: Student) => {
        setSelectedStudent(student);
        setStatusReason('');
        setShowStatusModal(true);
    };

    const confirmStatusChange = () => {
        if (!selectedStudent) return;

        const newStatus = selectedStudent.academicInfo.status === 'active' ? 'inactive' : 'active';
        onToggleStatus(selectedStudent.id, newStatus, statusReason);

        setShowStatusModal(false);
        setSelectedStudent(null);
        setStatusReason('');
    };

    const handleImageClick = (imageUrl: string, studentName: string) => {
        setSelectedImageUrl(imageUrl);
        setSelectedImageAlt(`${studentName} - Profile Image`);
        setShowImageModal(true);
    };

    return {
        searchTerm,
        setSearchTerm,
        filterStatus,
        setFilterStatus,
        currentPage,
        setCurrentPage,
        itemsPerPage,
        setItemsPerPage,
        viewMode,
        setViewMode,
        viewingStudent,
        setViewingStudent,
        showStatusModal,
        setShowStatusModal,
        selectedStudent,
        setSelectedStudent,
        statusReason,
        setStatusReason,
        showImageModal,
        setShowImageModal,
        selectedImageUrl,
        setSelectedImageUrl,
        selectedImageAlt,
        setSelectedImageAlt,
        filteredStudents,
        totalPages,
        startIndex,
        paginatedStudents,
        handlePageChange,
        handleItemsPerPageChange,
        handleStatusToggle,
        confirmStatusChange,
        handleImageClick,
    };
};
